/**
 * useSummary Hook
 *
 * Aggregated financial summary data for dashboard.
 * Supports custom date ranges for period navigation.
 */

import { useState, useEffect, useCallback } from 'react';
import { transactionService } from '@services/transactions';
import { accountService } from '@services/accounts';
import { dataEvents } from '@services/dataEvents';

interface SummaryData {
  totalBalance: number;
  monthIncome: number;
  monthExpense: number;
  monthNet: number;
  accountCount: number;
  transactionCount: number;
}

interface UseSummaryOptions {
  startDate?: Date;
  endDate?: Date;
}

interface UseSummaryResult {
  data: SummaryData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  dateRange: { start: Date; end: Date };
}

/**
 * Get start and end of current month.
 */
function getCurrentMonthRange(): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return { start, end };
}

/**
 * Get "all time" range - from very old date to now.
 */
function getAllTimeRange(): { start: Date; end: Date } {
  const start = new Date(2000, 0, 1); // Far back enough for any data
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Hook for dashboard summary data.
 * @param options - Optional startDate/endDate to override default (current month)
 *                  Pass undefined dates for "all time" range.
 */
export function useSummary(options?: UseSummaryOptions): UseSummaryResult {
  const [data, setData] = useState<SummaryData>({
    totalBalance: 0,
    monthIncome: 0,
    monthExpense: 0,
    monthNet: 0,
    accountCount: 0,
    transactionCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine date range:
  // - If both dates provided, use them
  // - If no options or both undefined, use "all time"
  // - Otherwise fall back to current month
  const allTimeRange = getAllTimeRange();
  const defaultRange = getCurrentMonthRange();

  let dateRange: { start: Date; end: Date };
  if (options) {
    // Options provided
    if (options.startDate === undefined && options.endDate === undefined) {
      // Both undefined means "All Time"
      dateRange = allTimeRange;
    } else {
      // Use provided dates, fall back to current month for missing
      dateRange = {
        start: options.startDate || defaultRange.start,
        end: options.endDate || defaultRange.end,
      };
    }
  } else {
    // No options = current month
    dateRange = defaultRange;
  }

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // NOTE: Removed auto-recalculation - balances are set manually or updated when transactions change

      // Fetch data in parallel
      const [totalBalance, totals, accountCount, transactionCount] = await Promise.all([
        accountService.getTotalBalance(),
        transactionService.getTotals(dateRange.start, dateRange.end),
        accountService.count(),
        transactionService.count(),
      ]);

      setData({
        totalBalance,
        monthIncome: totals.income,
        monthExpense: totals.expense,
        monthNet: totals.income - totals.expense,
        accountCount,
        transactionCount,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load summary';
      setError(message);
      console.error('[useSummary] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.start.getTime(), dateRange.end.getTime()]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Auto-refresh when accounts or transactions change
  useEffect(() => {
    const unsubAccounts = dataEvents.on('accounts', fetchSummary);
    const unsubTransactions = dataEvents.on('transactions', fetchSummary);
    return () => {
      unsubAccounts();
      unsubTransactions();
    };
  }, [fetchSummary]);

  return {
    data,
    loading,
    error,
    refresh: fetchSummary,
    dateRange,
  };
}
